import { type ApolloError, useApolloClient } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { usePage } from 'src/components/Router/usePage';
import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import {
  GetPersonalDetailsDocument,
  type Maybe,
  type PersonalDetails,
  UpdatePersonalDetailsDocument,
  type UpdatePersonalDetailsMutationVariables,
} from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  updatePersonalDetailsFailure,
  updatePersonalDetailsSuccess,
} from './actionCreators';
import {
  generateUpdatePersonalDetailsOptimisticResponse,
  updateCacheUpdatePersonalDetails,
} from './apolloCache';
import type {
  CurrentLocation2,
  CurrentLocation2AutosuggestData,
} from './types';
import { useUpdatePersonalDetailsAnalytics } from './useUpdatePersonalDetailsAnalytics';

export const usePersonalDetails = () => {
  const { locale, language, zone } = useAppConfig();

  const { loading, error, data } = useQuery(GetPersonalDetailsDocument, {
    variables: {
      countryCode: locale,
      languageCode: language,
      zone,
    },
  });

  const personalDetails = loading || error ? null : data?.viewer || null;

  return { personalDetails, loading, error };
};

const key = 'personal-details';

export const useUpdatePersonalDetails = () => {
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();
  const {
    locale: countryCode,
    language: languageCode,
    zone,
    isUnifiedSite,
  } = useAppConfig();
  const page = usePage();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const { updatePersonalDetailsAnalytics } =
    useUpdatePersonalDetailsAnalytics();

  const onCompleted = () => {
    dispatch(updatePersonalDetailsSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(updatePersonalDetailsFailure(error));
  };

  const onMutate = (
    _variables: UpdatePersonalDetailsMutationVariables,
    mutationMetadata?: {
      updatedCurrentLocation2?: CurrentLocation2AutosuggestData;
      updatedPersonalDetailsSections?: string[];
    },
  ) => {
    if (mutationMetadata) {
      const { updatedCurrentLocation2, updatedPersonalDetailsSections } =
        mutationMetadata;

      if (updatedPersonalDetailsSections?.length) {
        updatePersonalDetailsAnalytics({
          updatedPersonalDetailsSections,
          updatedLocation: {
            kind: updatedCurrentLocation2?.kind,
            countryCode: updatedCurrentLocation2?.countryCode,
          },
        });
      }
    }
  };

  return useToastMutation(UpdatePersonalDetailsDocument, {
    key,
    errorActionRouteParameters: { card: 'personal-details', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Personal details updated'),
    update: updateCacheUpdatePersonalDetails(languageCode, countryCode, zone),
    optimisticResponse: generateUpdatePersonalDetailsOptimisticResponse({
      _id: candidateId || '',
      page,
      apolloClient,
      zone,
      isUnifiedSite,
    }),
  });
};

export const mockGetPersonalDetailsRequest = ({
  emailAddress,
  personalDetails,
  currentLocation2,
}: {
  emailAddress: string;
  personalDetails: PersonalDetails;
  currentLocation2: Maybe<CurrentLocation2>;
}) => ({
  request: {
    query: GetPersonalDetailsDocument,
    variables: {
      languageCode: 'en',
      countryCode: 'AU',
      zone: 'anz-1',
    },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        emailAddress,
        personalDetails,
        currentLocation2,
      },
    },
  },
});

export const commonMockGetPersonalDetailsRequestResult = {
  _id: '1234',
  __typename: 'Candidate',
  emailAddress: 'test@test.com',
  personalDetails: {
    __typename: 'PersonalDetails',
    firstName: 'Mock First',
    lastName: 'Mock Last',
    countryCallingCode: {
      __typename: 'CountryCallingCode',
      id: '1',
      description: 'Mock country calling code',
      countryCallingCode: '1',
    },
    defaultCountryCallingCode: {
      __typename: 'CountryCallingCode',
      id: '2',
      description: 'Mock default country calling code',
      countryCallingCode: '2',
    },
    nationality: {
      __typename: 'Nationality',
      id: '3',
      description: 'Mock nationality',
    },
    phoneNumber: null,
  },
  currentLocation2: {
    __typename: 'Location2',
    id: 18578,
    description: 'Melbourne VIC 3000',
    shortDescription: 'Melbourne, Victoria',
    kind: 'Suburb',
    countryCode: 'AU',
    isDeprecated: false,
  },
};
